import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Video, CalendarDays, PartyPopper, Megaphone, X } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  online_class: Video,
  notification: Bell,
  holidays: CalendarDays,
  events: PartyPopper,
  broadcast: Megaphone,
};

export const UpdatesTicker = ({ endpoint }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const timer = useRef(null);
  const dismissKey = `ticker_dismissed::${endpoint}`;

  const getDismissed = useCallback(() => {
    try { return new Set(JSON.parse(localStorage.getItem(dismissKey) || "[]")); }
    catch { return new Set(); }
  }, [dismissKey]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      const dismissed = getDismissed();
      setItems((data.items || []).filter((it) => !dismissed.has(String(it.id))));
    } catch (_) { /* silent */ }
  }, [endpoint, getDismissed]);

  // Remove an item once it's been acted on (opened) — and remember it so the
  // 60s refresh / page reload doesn't bring it back.
  const dismiss = useCallback((it) => {
    const dismissed = getDismissed();
    dismissed.add(String(it.id));
    try { localStorage.setItem(dismissKey, JSON.stringify([...dismissed].slice(-300))); } catch { /* ignore */ }
    setItems((prev) => prev.filter((x) => String(x.id) !== String(it.id)));
  }, [dismissKey, getDismissed]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    const onRefresh = () => load();
    window.addEventListener("ticker:refresh", onRefresh);
    return () => { clearInterval(timer.current); window.removeEventListener("ticker:refresh", onRefresh); };
  }, [load]);

  if (!items.length) return null;

  // Duplicate the list so the marquee scrolls seamlessly.
  const loop = [...items, ...items];
  const duration = Math.max(18, items.length * 6);

  return (
    <div
      className="md:pl-64 bg-slate-900 dark:bg-black text-slate-100 border-b border-slate-800"
      data-testid="updates-ticker"
    >
      <div className="relative flex items-center overflow-hidden h-9">
        <span className="shrink-0 z-10 h-full px-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-sky-500 text-white">
          <Megaphone className="h-3.5 w-3.5" /> Updates
        </span>
        <div className="ticker-mask relative flex-1 overflow-hidden h-full">
          <div
            className="ticker-track flex items-center gap-8 h-full whitespace-nowrap will-change-transform"
            style={{ animation: `ticker-scroll ${duration}s linear infinite` }}
          >
            {loop.map((it, i) => {
              const Icon = ICONS[it.kind] || Bell;
              return (
                <span
                  key={`${it.id}-${i}`}
                  className="group inline-flex items-center gap-2 text-[13px] text-slate-200"
                  data-testid={`ticker-item-${it.kind}`}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (it.link) navigate(it.link); dismiss(it); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { if (it.link) navigate(it.link); dismiss(it); } }}
                    className={`inline-flex items-center gap-2 ${it.link ? "cursor-pointer" : "cursor-default"} hover:text-white transition-colors`}
                    title={it.link ? "Open (removes it from Updates)" : "Update"}
                  >
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${it.kind === "online_class" ? "bg-rose-500/20 text-rose-300" : it.kind === "notification" ? "bg-sky-500/20 text-sky-300" : it.kind === "broadcast" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <span className={`${it.link ? "group-hover:underline" : ""} underline-offset-2`}>{it.text}</span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); dismiss(it); }}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 hover:text-white hover:bg-white/20 transition-colors"
                    title="Dismiss"
                    aria-label="Dismiss update"
                    data-testid={`ticker-dismiss-${it.kind}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <span className="text-slate-600">•</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ticker-track:hover, .ticker-track:active { animation-play-state: paused !important; }
      `}</style>
    </div>
  );
};

export default UpdatesTicker;
